import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useDevMode } from "@/context/DevModeContext";

/**
 * Hidden-in-plain-sight dev tools. A tiny, low-opacity dot pinned to the
 * top-left corner of every page; clicking it opens the Dev options panel.
 * Easy to miss unless you know it's there. The master switch is "Dev mode" —
 * when on, dev-only affordances appear across the app (sample-test seeding on
 * the dashboard, the onboarding toggles on Build Blueprint, the failed-
 * generation sample loader). The dot turns amber while dev mode is active.
 */
export function DevOptions() {
  const { devMode, setDevMode } = useDevMode();

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Dev options"
          title="Dev options"
          className={`fixed top-2 left-2 z-100 h-3 w-3 rounded-full transition-colors ${
            devMode
              ? "bg-amber-400 hover:bg-amber-500"
              : "bg-muted-foreground/20 hover:bg-muted-foreground/80"
          }`}
          data-testid="button-dev-options"
        />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        className="w-64"
        data-testid="popover-dev-options"
      >
        <div className="space-y-3">
          <p className="text-sm font-semibold">Dev options</p>

          <button
            type="button"
            onClick={() => setDevMode(!devMode)}
            className="flex w-full items-center justify-between rounded-md border px-3 py-2 text-sm hover:bg-muted transition-colors"
            data-testid="toggle-dev-mode"
          >
            <span>Dev mode</span>
            <span
              className={`text-xs font-semibold ${devMode ? "text-amber-600" : "text-muted-foreground"}`}
            >
              {devMode ? "ON" : "OFF"}
            </span>
          </button>

          <p className="text-xs text-muted-foreground">
            {devMode
              ? "Dev affordances are showing across the app."
              : "Turn on to reveal sample-test seeding, onboarding toggles, and the failed-generation sample loader."}
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
